/* istanbul ignore file */

/**
 * Выбрасываем из coverage эту логику, потому что это легаси от которого в будущем откажемся,
 * и не понятно как это на самом деле покрыть тестами.
 */
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { Subscriber } from '@alfa-bank/site-event-bus/subscriber';
import { publish } from '@alfa-bank/site-event-bus';
import flow from 'lodash/flow';
import cookies from 'js-cookie';
import type { IGeoInfo } from '@alfa-bank/site-geo-info/src/models';

import { EVENTS } from '../config';
import type { TCitiesModalDesktop } from '../models';
import type { ICity } from '../features/model/http';

const getSubDomain = (host: string) => (host.includes('localhost') ? 'localhost' : `.${host}`);

interface IHandlerProps extends TCitiesModalDesktop {
    emittedBy: string;
}

const transformToOldGeo = (newCity: ICity): IGeoInfo => ({
    cityPath: newCity.path,
    cityTitle: newCity.cityNameRus,
    id: newCity.ajsonId,
    regionId: newCity.regionId,
});

const withHandler = (CitiesModal: FC<TCitiesModalDesktop>) => {
    function HandlerWrapper({ emittedBy, geo, open, ...rest }: IHandlerProps) {
        const onClose = useCallback(() => {
            publish('show_cities_popup', { show: false });
        }, []);
        const onChange = useCallback(
            (city: ICity) => {
                if (emittedBy === 'on_select_city_by_footer') {
                    cookies.set('site_city', city.path, {
                        expires: 30,
                        domain: getSubDomain(window.location.host),
                    });

                    /**
                     * Нам нужно, чтоб пользователь зашел с обновленной кукой, чтоб он получил новый контент из коллектора.
                     * Поэтому перезагружаем страницу. Не используем ссылки, потому что урл на деле никак не меняется.
                     */
                    window.location.reload();

                    return;
                }

                publish(emittedBy, transformToOldGeo(city));
                onClose();
            },
            [emittedBy, onClose]
        );

        return (
            <CitiesModal {...rest} geo={geo} onChange={onChange} onClose={onClose} open={open} />
        );
    }

    HandlerWrapper.displayName = `WithHandler(${CitiesModal.displayName || CitiesModal.name})`;

    return HandlerWrapper;
};

const withEvents = (HandlerComponent: FC<IHandlerProps>) => {
    function WithEvents(props: TCitiesModalDesktop) {
        return (
            <Subscriber events={EVENTS}>
                <HandlerComponent {...(props as IHandlerProps)} />
            </Subscriber>
        );
    }

    WithEvents.displayName = `WithEvents(${HandlerComponent.displayName || HandlerComponent.name})`;

    return WithEvents;
};

export const withEventBus = flow([withHandler, withEvents]);
